import React, { useState, useRef } from "react";
import { Row, Col, Form, Alert } from "react-bootstrap";
import { motion } from "framer-motion";
import {
  FaUser,
  FaCalendarAlt,
  FaGlobe,
  FaIdCard,
  FaEnvelope,
  FaPhone,
  FaHome,
  FaUpload,
  FaCheck,
  FaTimes,
} from "react-icons/fa";

const PersonalInfoAndAddressStep = ({
  formData,
  updateFormData,
  errors,
  nextStep,
  currentStep,
}) => {
  const personalInfo = formData.personalInfo || {};
  const address = formData.address || {};
  const fileInputRef = useRef(null);
  const [dragActive, setDragActive] = useState(false);
  const [uploadStatus, setUploadStatus] = useState("");

  const handlePersonalInfoChange = (e) => {
    const { name, value } = e.target;
    updateFormData("personalInfo", { [name]: value });
  };

  const handleAddressChange = (e) => {
    const { name, value } = e.target;
    updateFormData("address", { [name]: value });
  };

  const handleFileUpload = (files) => {
    if (files && files[0]) {
      const file = files[0];

      // Validate file type
      const allowedTypes = [
        "image/jpeg",
        "image/png",
        "image/jpg",
        "application/pdf",
      ];
      if (!allowedTypes.includes(file.type)) {
        setUploadStatus("error");
        return;
      }

      // Validate file size (5MB max)
      if (file.size > 5 * 1024 * 1024) {
        setUploadStatus("error");
        return;
      }

      // Convert to base64 for storage
      const reader = new FileReader();
      reader.onload = (e) => {
        updateFormData("address", {
          proofOfAddress: {
            name: file.name,
            type: file.type,
            size: file.size,
            data: e.target.result,
          },
        });
        setUploadStatus("success");
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    handleFileUpload(e.dataTransfer.files);
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const countries = [
    "United States",
    "Canada",
    "United Kingdom",
    "Germany",
    "France",
    "Italy",
    "Spain",
    "Netherlands",
    "Switzerland",
    "Australia",
    "Japan",
    "Singapore",
    "Hong Kong",
    "Other",
  ];

  const genderOptions = [
    { value: "", label: "Prefer not to say" },
    { value: "male", label: "Male" },
    { value: "female", label: "Female" },
    { value: "other", label: "Other" },
  ];

  const maritalStatusOptions = [
    { value: "", label: "Select..." },
    { value: "single", label: "Single" },
    { value: "married", label: "Married" },
    { value: "divorced", label: "Divorced" },
    { value: "widowed", label: "Widowed" },
    { value: "other", label: "Other" },
  ];

  const documentTypes = [
    "Utility Bill (Electricity, Gas, Water)",
    "Bank Statement",
    "Internet/Cable Bill",
    "Cell Phone Bill",
    "Government Notice",
    "Insurance Statement",
    "Rental Agreement",
    "Other Official Document",
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <style jsx>{`
        .file-upload-area {
          border: 2px dashed #dee2e6;
          border-radius: 8px;
          padding: 2rem;
          text-align: center;
          cursor: pointer;
          transition: all 0.3s ease;
          background: rgba(255, 255, 255, 0.05);
          margin-top: 0.5rem;
        }

        .file-upload-area:hover {
          border-color: #007bff;
          background: rgba(0, 123, 255, 0.1);
        }

        .file-upload-area.drag-active {
          border-color: #28a745;
          background: rgba(40, 167, 69, 0.1);
        }

        .file-upload-area.has-file {
          border-color: #28a745;
          background: rgba(40, 167, 69, 0.05);
        }

        .upload-icon {
          font-size: 3rem;
          color: #6c757d;
          margin-bottom: 1rem;
        }

        .file-info {
          padding: 1rem;
          background: rgba(40, 167, 69, 0.1);
          border-radius: 6px;
        }
      `}</style>
      <Form>
        {/* Personal Information Section */}
        <Row>
          <Col md={12} className="mb-4">
            <h4 className="mb-3 text-primary">Personal Information</h4>
          </Col>

          <Col md={12} className="mb-4">
            <div className="form-group">
              <label className="form-label d-flex align-items-center">
                <FaUser className="me-2 text-primary" />
                Full Name <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                name="fullName"
                value={personalInfo.fullName || ""}
                onChange={handlePersonalInfoChange}
                className={`form-control ${
                  errors.fullName ? "is-invalid" : ""
                }`}
                placeholder="Enter your full legal name"
                required
              />
              {errors.fullName && (
                <div className="invalid-feedback">{errors.fullName}</div>
              )}
              <small className="form-text text-muted">
                Enter your name exactly as it appears on your government-issued
                ID
              </small>
            </div>
          </Col>

          <Col md={6}>
            <div className="form-group">
              <label className="form-label d-flex align-items-center">
                <FaCalendarAlt className="me-2 text-primary" />
                Date of Birth <span className="text-danger">*</span>
              </label>
              <input
                type="date"
                name="dateOfBirth"
                value={personalInfo.dateOfBirth || ""}
                onChange={handlePersonalInfoChange}
                className={`form-control ${
                  errors.dateOfBirth ? "is-invalid" : ""
                }`}
                required
              />
              {errors.dateOfBirth && (
                <div className="invalid-feedback">{errors.dateOfBirth}</div>
              )}
            </div>
          </Col>

          <Col md={6}>
            <div className="form-group">
              <label className="form-label d-flex align-items-center">
                <FaGlobe className="me-2 text-primary" />
                Nationality <span className="text-danger">*</span>
              </label>
              <select
                name="nationality"
                value={personalInfo.nationality || ""}
                onChange={handlePersonalInfoChange}
                className={`form-control ${
                  errors.nationality ? "is-invalid" : ""
                }`}
                required
              >
                <option value="">Select your nationality</option>
                {countries.map((country) => (
                  <option key={country} value={country}>
                    {country}
                  </option>
                ))}
              </select>
              {errors.nationality && (
                <div className="invalid-feedback">{errors.nationality}</div>
              )}
            </div>
          </Col>

          <Col md={6}>
            <div className="form-group">
              <label className="form-label">Gender (Optional)</label>
              <select
                name="gender"
                value={personalInfo.gender || ""}
                onChange={handlePersonalInfoChange}
                className="form-control"
              >
                {genderOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </Col>

          <Col md={6}>
            <div className="form-group">
              <label className="form-label">Marital Status</label>
              <select
                name="maritalStatus"
                value={personalInfo.maritalStatus || ""}
                onChange={handlePersonalInfoChange}
                className="form-control"
              >
                {maritalStatusOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </Col>

          <Col md={12}>
            <div className="form-group">
              <label className="form-label d-flex align-items-center">
                <FaIdCard className="me-2 text-primary" />
                Identification Number <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                name="idNumber"
                value={personalInfo.idNumber || ""}
                onChange={handlePersonalInfoChange}
                className={`form-control ${
                  errors.idNumber ? "is-invalid" : ""
                }`}
                placeholder="Passport, National ID, or Driver's License number"
                required
              />
              {errors.idNumber && (
                <div className="invalid-feedback">{errors.idNumber}</div>
              )}
              <small className="form-text text-muted">
                Enter your passport number, national ID, or driver's license
                number
              </small>
            </div>
          </Col>

          <Col md={6}>
            <div className="form-group">
              <label className="form-label d-flex align-items-center">
                <FaEnvelope className="me-2 text-primary" />
                Email Address <span className="text-danger">*</span>
              </label>
              <input
                type="email"
                name="email"
                value={personalInfo.email || ""}
                onChange={handlePersonalInfoChange}
                className={`form-control ${errors.email ? "is-invalid" : ""}`}
                placeholder="your.email@example.com"
                required
              />
              {errors.email && (
                <div className="invalid-feedback">{errors.email}</div>
              )}
            </div>
          </Col>

          <Col md={6}>
            <div className="form-group">
              <label className="form-label d-flex align-items-center">
                <FaPhone className="me-2 text-primary" />
                Phone Number <span className="text-danger">*</span>
              </label>
              <input
                type="tel"
                name="phone"
                value={personalInfo.phone || ""}
                onChange={handlePersonalInfoChange}
                className={`form-control ${errors.phone ? "is-invalid" : ""}`}
                placeholder="+1 (555) 123-4567"
                required
              />
              {errors.phone && (
                <div className="invalid-feedback">{errors.phone}</div>
              )}
              <small className="form-text text-muted">
                Include country code (e.g., +1 for US/Canada)
              </small>
            </div>
          </Col>

          {/* Address Information Section */}
          <Col md={12} className="my-4">
            <hr />
            <h4 className="mb-3 text-primary">Address Information</h4>
          </Col>

          <Col md={12}>
            <div className="form-group">
              <label className="form-label d-flex align-items-center">
                <FaHome className="me-2 text-primary" />
                Street Address <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                name="street"
                value={address.street || ""}
                onChange={handleAddressChange}
                className={`form-control ${errors.street ? "is-invalid" : ""}`}
                placeholder="Enter your full street address"
                required
              />
              {errors.street && (
                <div className="invalid-feedback">{errors.street}</div>
              )}
            </div>
          </Col>

          <Col md={6}>
            <div className="form-group">
              <label className="form-label">
                City <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                name="city"
                value={address.city || ""}
                onChange={handleAddressChange}
                className={`form-control ${errors.city ? "is-invalid" : ""}`}
                placeholder="Enter your city"
                required
              />
              {errors.city && (
                <div className="invalid-feedback">{errors.city}</div>
              )}
            </div>
          </Col>

          <Col md={6}>
            <div className="form-group">
              <label className="form-label">
                State/Province <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                name="state"
                value={address.state || ""}
                onChange={handleAddressChange}
                className={`form-control ${errors.state ? "is-invalid" : ""}`}
                placeholder="Enter your state or province"
                required
              />
              {errors.state && (
                <div className="invalid-feedback">{errors.state}</div>
              )}
            </div>
          </Col>

          <Col md={6}>
            <div className="form-group">
              <label className="form-label">
                Postal Code <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                name="postalCode"
                value={address.postalCode || ""}
                onChange={handleAddressChange}
                className={`form-control ${
                  errors.postalCode ? "is-invalid" : ""
                }`}
                placeholder="Enter your postal/ZIP code"
                required
              />
              {errors.postalCode && (
                <div className="invalid-feedback">{errors.postalCode}</div>
              )}
            </div>
          </Col>

          <Col md={6}>
            <div className="form-group">
              <label className="form-label">
                Country <span className="text-danger">*</span>
              </label>
              <select
                name="country"
                value={address.country || ""}
                onChange={handleAddressChange}
                className={`form-control ${errors.country ? "is-invalid" : ""}`}
                required
              >
                <option value="">Select your country</option>
                {countries.map((country) => (
                  <option key={country} value={country}>
                    {country}
                  </option>
                ))}
              </select>
              {errors.country && (
                <div className="invalid-feedback">{errors.country}</div>
              )}
            </div>
          </Col>

          <Col md={12}>
            <div className="form-group">
              <label className="form-label">Document Type</label>
              <select
                name="documentType"
                value={address.documentType || ""}
                onChange={handleAddressChange}
                className="form-control"
              >
                <option value="">Select document type</option>
                {documentTypes.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
              <small className="form-text text-muted">
                Select the type of document you're uploading as proof of address
              </small>
            </div>
          </Col>

          <Col md={12}>
            <div className="form-group">
              <label className="form-label">
                Proof of Address{" "}
                <span className="text-muted">
                  (Optional - can be uploaded later)
                </span>
              </label>

              <div
                className={`file-upload-area ${
                  dragActive ? "drag-active" : ""
                } ${address.proofOfAddress ? "has-file" : ""}`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".jpg,.jpeg,.png,.pdf"
                  onChange={(e) => handleFileUpload(e.target.files)}
                  style={{ display: "none" }}
                />

                {!address.proofOfAddress ? (
                  <div className="upload-content text-center">
                    <FaUpload className="upload-icon mb-3" />
                    <h5>Upload Proof of Address</h5>
                    <p className="text-muted mb-2">
                      Drag and drop your document here, or click to browse
                    </p>
                    <small className="text-muted">
                      Accepted formats: JPG, PNG, PDF (Max 5MB)
                    </small>
                  </div>
                ) : (
                  <div className="file-info d-flex align-items-center justify-content-between">
                    <div className="d-flex align-items-center">
                      <FaCheck className="text-success me-2" />
                      <div>
                        <strong>{address.proofOfAddress.name}</strong>
                        <small className="d-block text-muted">
                          {(address.proofOfAddress.size / 1024 / 1024).toFixed(
                            2
                          )}{" "}
                          MB
                        </small>
                      </div>
                    </div>
                    <button
                      type="button"
                      className="btn btn-outline-danger btn-sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        updateFormData("address", { proofOfAddress: null });
                        setUploadStatus("");
                      }}
                    >
                      <FaTimes />
                    </button>
                  </div>
                )}
              </div>

              {uploadStatus === "error" && (
                <Alert variant="danger" className="mt-2 mb-0">
                  Invalid file. Please upload a JPG, PNG, or PDF file under 5MB.
                </Alert>
              )}

              <small className="form-text text-muted">
                Upload a recent utility bill, bank statement, or other official
                document showing your name and address (dated within last 3
                months).
                <br />
                <strong>Don't have it handy?</strong> You can continue with the
                KYC process and upload this document later from your account
                dashboard.
              </small>
            </div>
          </Col>
        </Row>
      </Form>
    </motion.div>
  );
};

export default PersonalInfoAndAddressStep;
